"use client";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";

import type { IntegrationInfoContent } from "./integration-info-content";
import {
  QuickBooksSyncFlowDiagram,
  StripeEmailPayFlowDiagram,
  StripePaymentFlowDiagram,
} from "./integration-info-illustrations";

interface IntegrationInfoDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  content: IntegrationInfoContent;
}

function IntegrationInfoIllustration({
  integrationId,
}: {
  integrationId: IntegrationInfoContent["id"];
}) {
  if (integrationId === "quickbooks") {
    return <QuickBooksSyncFlowDiagram />;
  }

  if (integrationId === "stripe") {
    return (
      <div className="space-y-3">
        <StripePaymentFlowDiagram />
        <StripeEmailPayFlowDiagram />
      </div>
    );
  }

  return null;
}

function IntegrationInfoSectionCard({
  section,
}: {
  section: IntegrationInfoContent["sections"][number];
}) {
  const SectionIcon = section.icon;

  return (
    <section className="rounded-xl border bg-card/50 p-4 shadow-sm">
      <div className="mb-3 flex items-center gap-2.5">
        <div
          className={cn(
            "flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-muted/60",
            section.accentClass
          )}
        >
          <SectionIcon className="h-4 w-4" aria-hidden />
        </div>
        <h3 className="text-sm font-semibold">{section.heading}</h3>
      </div>

      {section.paragraphs?.map((paragraph) => (
        <p
          key={paragraph}
          className="text-sm leading-relaxed text-muted-foreground"
        >
          {paragraph}
        </p>
      ))}

      {section.items && section.items.length > 0 ? (
        <ul
          className={cn(
            "space-y-2",
            section.paragraphs?.length ? "mt-3" : undefined
          )}
        >
          {section.items.map((item) => {
            const ItemIcon = item.icon;
            return (
              <li
                key={item.title}
                className="flex gap-3 rounded-lg border border-transparent bg-muted/30 px-3 py-2.5 transition-colors hover:border-border/60"
              >
                <div
                  className={cn(
                    "mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-background shadow-sm",
                    section.accentClass
                  )}
                >
                  <ItemIcon className="h-3.5 w-3.5" aria-hidden />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-medium leading-snug">{item.title}</p>
                  {item.description ? (
                    <p className="mt-0.5 text-xs leading-relaxed text-muted-foreground">
                      {item.description}
                    </p>
                  ) : null}
                </div>
              </li>
            );
          })}
        </ul>
      ) : null}
    </section>
  );
}

export function IntegrationInfoDialog({
  open,
  onOpenChange,
  content,
}: IntegrationInfoDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[90vh] flex-col gap-0 overflow-hidden p-0 sm:max-w-[54rem]">
        <div
          className={cn(
            "shrink-0 border-b px-6 pb-5 pt-6",
            content.headerClassName
          )}
        >
          <div className="flex items-start gap-4">
            <div
              className={cn(
                "flex h-14 w-14 shrink-0 items-center justify-center rounded-xl border shadow-sm ring-4",
                content.iconRingClassName
              )}
            >
              <img
                src={content.logoSrc}
                alt=""
                className="h-9 w-9 object-contain"
              />
            </div>
            <div className="min-w-0 pt-0.5">
              <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Integration
              </p>
              <h2 className="text-xl font-semibold tracking-tight">
                {content.title}
              </h2>
              <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">
                {content.summary}
              </p>
            </div>
          </div>
        </div>

        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-6 py-5">
          <IntegrationInfoIllustration integrationId={content.id} />

          {content.sections.map((section) => (
            <IntegrationInfoSectionCard key={section.id} section={section} />
          ))}
        </div>

        <DialogFooter className="shrink-0 border-t bg-muted/20 px-6 py-4">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
